/**
 * A module's board, on its own screen.
 *
 * It is a screen rather than a tab or a sheet because three different people open
 * it for three different reasons — a learner at the end of a run, a teacher from a
 * card in their feed, and anyone at all from the module's own page — and all three
 * want the numbers *without* the playlist around them. It is also the one place the
 * gate is explained properly: under a hundred finishers the screen says what the
 * wait is for, which is a sentence that has no room on a card.
 *
 * The board itself is `#/components/modules/ModuleLeaderboardCard`; this screen
 * owns the frame — the header, the module's name, the preview switcher in a dev
 * build, and the way back into playing the module, which is what a learner who has
 * just looked at the standings usually wants next.
 */
import {useCallback} from 'react'
import {Trans, useLingui} from '@lingui/react/macro'

import {modulePreviewStateOf, readModulePreviewMode} from '#/lib/moduleFixtures'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {atoms as a, useTheme} from '#/alf'
import {moduleUriFromPath} from '#/lib/moduleRecord'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {Play_Filled_Corner0_Rounded as Play} from '#/components/icons/Play'
import * as Layout from '#/components/Layout'
import {ModuleLeaderboardCard} from '#/components/modules/ModuleLeaderboardCard'
import {ModulePreviewBar} from '#/components/modules/ModulePreviewBar'
import {useModuleBoard} from '#/components/modules/useModuleBoard'
import {Text} from '#/components/Typography'
import {IS_DEV} from '#/env'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'ModuleLeaderboard'>

export function ModuleLeaderboardScreen({route, navigation}: Props) {
  /** Decoded once: a module's address arrives percent-encoded off a URL. */
  const moduleUri = moduleUriFromPath(route.params.moduleUri) ?? ''
  const {t: l} = useLingui()
  const t = useTheme()

  /*
   * The preview door, read only in a dev build and only while preview mode is on.
   *
   * A production build cannot reach an invented board even by URL: the param is
   * parsed here behind `IS_DEV`, and with it comes out `null` — the class's own
   * plays are the only thing the screen can show. The mode check is the second
   * lock, and it is the reviewer's rather than the build's: with the preview turned
   * off in the settings screen, `?preview=board` means the real board too, and the
   * row of chips is not drawn at all (`#/lib/moduleFixtures`).
   */
  const previewMode = IS_DEV && readModulePreviewMode()
  const preview = previewMode
    ? modulePreviewStateOf(route.params.preview)
    : null

  const {record, board, loading, failed, isPreview} = useModuleBoard({
    moduleUri,
    preview,
  })

  const onSetPreview = useCallback(
    (next: typeof preview) => {
      navigation.setParams({preview: next ?? undefined})
    },
    [navigation],
  )

  const title = record?.title || l`Module`

  return (
    <Layout.Screen testID="moduleLeaderboardScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Leaderboard</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>

      <Layout.Content
        testID="moduleLeaderboardContent"
        contentContainerStyle={[a.px_md, a.pb_4xl, a.gap_md]}>
        <Text style={[a.text_2xl, a.font_bold, a.leading_tight, t.atoms.text]}>
          {title}
        </Text>

        {previewMode ? (
          <ModulePreviewBar state={preview} onChange={onSetPreview} />
        ) : null}

        <ModuleLeaderboardCard
          title={title}
          board={board}
          currentDid={undefined}
          loading={loading}
          failed={failed}
        />

        {/*
          The way back into the module, under the board.
          
          A learner who has looked at the standings has almost certainly come to
          play — from the card in a feed, or after a run — so the door stands where
          the eye lands last rather than somewhere in the header.
        */}
        <Button
          testID="moduleLeaderboardPlay"
          label={board.mode === 'results' ? l`Play module` : l`Play again`}
          size="large"
          color="primary"
          onPress={() => navigation.navigate('ModulePlay', {moduleUri})}>
          <ButtonIcon icon={Play} />
          <ButtonText>
            <Trans>Play module</Trans>
          </ButtonText>
        </Button>

        {isPreview ? (
          <Text
            style={[a.text_xs, a.text_center, t.atoms.text_contrast_medium]}>
            <Trans>
              This board is a preview: the plays on it were invented for
              testing.
            </Trans>
          </Text>
        ) : null}
      </Layout.Content>
    </Layout.Screen>
  )
}
